import {
  doc, getDoc, setDoc, runTransaction, deleteField,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { SealedRecord, WrappedDek, KdfParams } from './crypto';
import {
  VaultStaleWriteError, VaultKeysCorruptError, VaultWrapRaceError, VaultKeysMovedError,
} from './vaultErrors';

// Re-exported so existing importers are unaffected; defined in vaultErrors.ts, which has no
// imports, so a test can reference them without booting the Firebase SDK.
export { VaultStaleWriteError, VaultKeysCorruptError, VaultWrapRaceError, VaultKeysMovedError };

/**
 * Reading and writing the vault's ciphertext.
 *
 * ── LAYOUT ──────────────────────────────────────────────────────────────────────────────────────
 *
 *   classes/{classId}/vault/roster          → { records: { [studentId]: SealedRecord } }
 *   users/{uid}/private/vaultKeys           → { passphrase: WrappedDek, recovery?: WrappedDek,
 *                                               classes: { [classId]: WrappedDek } }
 *
 * `classes/{classId}/vault/*` has its OWN rules match, owner-only read and write. It is deliberately not
 * under `classes/{classId}/private/*`, which grants co-teachers and school admins `allow write`: a
 * co-teacher holds no key, so write access there buys nothing and risks everything — one overwrite and
 * the name mapping is gone with no plaintext copy anywhere to rebuild from.
 *
 * ── WHY RECORDS LIVE IN ONE DOCUMENT, KEYED BY STUDENT ──────────────────────────────────────────
 *
 * One document per student would be cleanest, but a roster read is then N reads on every class open, and
 * Firestore rules cannot iterate a collection to prove no document is missing. One document with a map
 * keyed by `studentId` gives a single read, and field-level merges mean two devices editing DIFFERENT
 * students never collide. Same-student collisions are caught by the revision check below.
 */

const rosterRef = (classId: string) => doc(db, 'classes', classId, 'vault', 'roster');
const keysRef = (uid: string) => doc(db, 'users', uid, 'private', 'vaultKeys');

export interface VaultRoster {
  records: Record<string, SealedRecord>;
}

export interface VaultKeys {
  /**
   * The KDF parameters for THIS TEACHER, used once to derive the KEK that opens every class wrap below.
   *
   * One set, not one per class: a per-class salt made unlocking fifteen classes fifteen PBKDF2 derivations
   * at 600,000 iterations — about thirty seconds — and made a cross-class screen impossible to open.
   */
  kdf: KdfParams;
  /** The class keys, each wrapped under the passphrase-derived KEK. */
  classes: Record<string, WrappedDek>;
  /** Separate params for the recovery-code KEK; a recovery code is not the passphrase. */
  recoveryKdf?: KdfParams;
  /** The same class keys wrapped under the recovery-code KEK. */
  recovery?: Record<string, WrappedDek>;
}

/** The whole encrypted roster for a class. Absent is an ordinary state: nothing vaulted yet. */
export async function readVaultRoster(classId: string): Promise<VaultRoster> {
  const snap = await getDoc(rosterRef(classId));
  if (!snap.exists()) return { records: {} };
  const data = snap.data() as Partial<VaultRoster>;
  return { records: data.records ?? {} };
}

/**
 * Write one student's record, refusing to clobber a newer one.
 *
 * The transaction is the point. Two devices editing the SAME student would otherwise be last-write-wins,
 * and the loser's edit would vanish with nothing to indicate it — the roster being exactly the thing a
 * teacher touches from a laptop and a phone in the same week. A stale write is rejected so the caller can
 * re-read and retry against the current record rather than silently winning.
 *
 * Returns the revision actually stored.
 */
export async function writeVaultRecord(
  classId: string,
  studentId: string,
  record: SealedRecord,
): Promise<number> {
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(rosterRef(classId));
    const current = snap.exists()
      ? ((snap.data() as Partial<VaultRoster>).records ?? {})[studentId]
      : undefined;

    if (current && current.rev >= record.rev) {
      throw new VaultStaleWriteError(studentId, current.rev, record.rev);
    }

    // A field-level merge, so a concurrent write to a DIFFERENT student is untouched.
    tx.set(rosterRef(classId), { records: { [studentId]: record } }, { merge: true });
    return record.rev;
  });
}


/**
 * Write several records in one transaction, for an import.
 *
 * Importing thirty students one at a time is thirty transactions and thirty chances to half-finish. This
 * is all-or-nothing, and it still refuses to clobber: an import that would overwrite an existing newer
 * record for any student fails whole rather than partly applying.
 */
export async function writeVaultRecords(
  classId: string,
  records: Record<string, SealedRecord>,
): Promise<void> {
  const ids = Object.keys(records);
  if (!ids.length) return;
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(rosterRef(classId));
    const existing = snap.exists() ? ((snap.data() as Partial<VaultRoster>).records ?? {}) : {};
    for (const id of ids) {
      const cur = existing[id];
      if (cur && cur.rev >= records[id].rev) {
        throw new VaultStaleWriteError(id, cur.rev, records[id].rev);
      }
    }
    tx.set(rosterRef(classId), { records }, { merge: true });
  });
}

/**
 * Drop one student's record.
 *
 * `deleteField()` rather than writing null: a null would decode as a record whose every field is missing
 * and surface as a student whose name "failed to decrypt", which reads as corruption.
 */
export async function removeVaultRecord(classId: string, studentId: string): Promise<void> {
  await setDoc(rosterRef(classId), { records: { [studentId]: deleteField() } }, { merge: true });
}


/** The teacher's wrapped class keys. Absent means no vault has been set up on this account. */
export async function readVaultKeys(uid: string): Promise<VaultKeys | null> {
  const snap = await getDoc(keysRef(uid));
  if (!snap.exists()) return null;
  const data = snap.data() as Partial<VaultKeys>;

  /**
   * A document with wraps but no `kdf` is CORRUPT, not absent, and the difference matters enormously.
   *
   * Returning null made `setupClassVault` treat the account as fresh: new salt, new DEK, and a merge that
   * overwrote the existing wrap for that class while leaving every OTHER class's wrap derived from a salt
   * that no longer exists. That is a whole-account re-key — precisely what the rewrite promised never to do —
   * and the path to it is short, because `unlockWithSecret` on the same document throws "No vault has been
   * set up", whose natural UI response is the setup screen.
   */
  if (!data.kdf) {
    if (data.classes && Object.keys(data.classes).length) throw new VaultKeysCorruptError(uid);
    return null; // genuinely no vault yet
  }
  return {
    kdf: data.kdf,
    classes: data.classes ?? {},
    ...(data.recoveryKdf ? { recoveryKdf: data.recoveryKdf } : {}),
    ...(data.recovery ? { recovery: data.recovery } : {}),
  };
}

/**
 * Store a class key wrap.
 *
 * Merged per class so adding a second class never rewrites the first — losing an existing wrap locks the
 * teacher out of that class permanently, which is the single worst outcome this module can produce.
 *
 * ── WHY THIS IS A TRANSACTION AND NOT A MERGING setDoc ──────────────────────────────────────────
 *
 * It was a merging `setDoc`, which is atomic per field but has no idea what it is overwriting. Two devices
 * that both read "no wrap for this class" both wrote one, and the last won — see VaultWrapRaceError for
 * what that costs. A merge cannot express "only if absent", so the read and the write have to be in the
 * same transaction.
 *
 * Two preconditions, for two different races:
 *
 *   `expectAbsent` — refuse when a wrap for this class already exists. This is the concurrent-setup race,
 *   and it is the one that destroys data.
 *
 *   `expectKdfSalt` — refuse when the account's KDF salt has changed since the caller derived its KEK. A
 *   `changePassphrase` on another device rewrites the whole document with fresh params; a setup that read
 *   the OLD params and then merged would write them back, leaving every other class's wrap under a KEK the
 *   stored salt can no longer derive. The teacher's passphrase then fails on every class but this one,
 *   which is a lockout rather than a loss, but needs no more than this to prevent.
 */
export async function writeClassKeyWrap(
  uid: string,
  classId: string,
  args: {
    kdf: KdfParams;
    wrapped: WrappedDek;
    recoveryKdf?: KdfParams;
    recoveryWrapped?: WrappedDek;
    /** Refuse if a wrap for this class already exists. True for a first-time setup. */
    expectAbsent?: boolean;
    /** Refuse if the stored KDF salt differs from this. Pass what the caller derived from. */
    expectKdfSalt?: string;
  },
): Promise<void> {
  const ref = keysRef(uid);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const data = snap.exists() ? (snap.data() as Partial<VaultKeys>) : undefined;

    if (args.expectAbsent && data?.classes?.[classId]) {
      throw new VaultWrapRaceError(classId);
    }
    /**
     * COMPARED WHENEVER A SALT IS STORED — not only when the caller happened to have read one.
     *
     * This used to be `args.expectKdfSalt && data?.kdf?.salt && ...`, and `setupClassVault` passed
     * `existing?.kdf?.salt`, which is UNDEFINED on a brand-new account. So the guard was switched off on
     * the branch every new account starts on, which is exactly when a teacher is most likely to be
     * clicking "Protect this class" on two cards with two tabs open.
     *
     * Two tabs, two DIFFERENT classes, fresh account: tab 1 commits {kdf: saltA, classes:{c1}}; tab 2
     * passes `expectAbsent` because c2 really is absent, skips this check, and merges {kdf: saltB,
     * classes:{c2}} over the top. The stored salt is now B while c1's wrap is under KEK(saltA), and
     * `recoveryKdf` is overwritten the same way — so BOTH doors to c1 are gone, silently, with both tabs
     * reporting success and showing a recovery code.
     *
     * The caller now passes the salt it ACTUALLY DERIVED FROM, so the comparison is always meaningful:
     * if a salt is stored and it is not the one in hand, this write would strand whatever is already
     * wrapped under the stored one.
     */
    if (data?.kdf?.salt && data.kdf.salt !== args.expectKdfSalt) {
      throw new VaultWrapRaceError(classId);
    }

    tx.set(
      ref,
      {
        kdf: args.kdf,
        classes: { [classId]: args.wrapped },
        ...(args.recoveryKdf ? { recoveryKdf: args.recoveryKdf } : {}),
        ...(args.recoveryWrapped ? { recovery: { [classId]: args.recoveryWrapped } } : {}),
      },
      { merge: true },
    );
  });
}

/**
 * Remove a deleted class's key wraps from the teacher's keys document.
 *
 * ── WHY AN ORPHAN WRAP IS NOT HARMLESS ──────────────────────────────────────────────────────────
 *
 * Nothing removed these, so a wrap outlived the class it belonged to forever. That looks like
 * clutter and is actually a permanent lockout: `resetPassphraseWithRecoveryCodes` is all-or-nothing by
 * necessity — a partial re-wrap leaves the un-re-wrapped classes unopenable — so it iterates every
 * entry in `classes` and demands a recovery code for each. A teacher who deleted a class and threw its
 * code away could therefore NEVER reset a forgotten passphrase, blocked by a key for a class that no
 * longer exists, identified in the error only by a raw document id.
 *
 * Safe to delete outright: the class delete is a `recursiveDelete`, which takes
 * `classes/{id}/vault/roster` with it, so the wrap opens nothing by the time this runs. The warning
 * that the names cannot be recovered is given before the delete, not here.
 *
 * Best-effort by design — a teacher whose class delete succeeded must not be told it failed because a
 * tidy-up write did not land. The only cost of a leftover is the lockout above, and the reset names
 * which classes blocked it.
 */
export async function removeClassKeyWraps(uid: string, classId: string): Promise<void> {
  await setDoc(
    keysRef(uid),
    {
      classes: { [classId]: deleteField() },
      recovery: { [classId]: deleteField() },
    },
    { merge: true },
  );
}

/**
 * Re-wrap every class key after a passphrase change, in ONE write.
 *
 * Per-class writes would leave a half-changed account if the second failed: some classes opening with the
 * old passphrase and some with the new, and no way for the teacher to know which. The whole point of the
 * two-key envelope is that this is one small write rather than re-encrypting every roster.
 */
export async function replaceAllClassKeyWraps(
  uid: string,
  args: {
    kdf: KdfParams;
    classes: Record<string, WrappedDek>;
    recoveryKdf?: KdfParams;
    recovery?: Record<string, WrappedDek>;
    /** The class ids the caller read and believes it is replacing. Omit to skip the check. */
    expectReplacing?: readonly string[];
    /** The account KDF salt the caller read. Refuses if it has moved since. */
    expectKdfSalt?: string;
  },
): Promise<void> {
  const ref = keysRef(uid);
  /**
   * A TRANSACTION, because a blind whole-document write erases a class someone else just added.
   *
   * `expectKdfSalt` on the wrap write protects a setup that LOSES to a passphrase change. Nothing
   * protected a setup that WINS. A passphrase change reads the keys document and then spends two or more
   * PBKDF2 derivations — seconds, not milliseconds. A class protected on another device inside that
   * window commits successfully, recovery code shown to the teacher and all; then this `merge: false`
   * write replaces `classes` with the set that was read before it existed, and that class's passphrase
   * wrap AND recovery wrap are gone. The other device's cached key is then the only copy of its DEK, and
   * when that cache is evicted every name in the class is unrecoverable. No error, on either side.
   *
   * So the caller states what it believes it is replacing, and the write refuses if the document moved
   * underneath it. The caller re-reads and runs again, which costs a derivation and loses nothing.
   */
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const current = snap.exists() ? (snap.data() as Partial<VaultKeys>) : undefined;

    if (args.expectReplacing) {
      const now = Object.keys(current?.classes ?? {}).sort();
      const expected = [...args.expectReplacing].sort();
      const added = now.filter(id => !expected.includes(id));
      if (added.length) throw new VaultKeysMovedError(added);

      /**
       * The salt too. A second passphrase change landing between this one's read and write would be
       * overwritten silently, leaving the teacher's newest passphrase inert.
       */
      if (args.expectKdfSalt && current?.kdf?.salt && current.kdf.salt !== args.expectKdfSalt) {
        throw new VaultKeysMovedError([]);
      }
    }

    tx.set(ref, {
      kdf: args.kdf,
      classes: args.classes,
      ...(args.recoveryKdf ? { recoveryKdf: args.recoveryKdf } : {}),
      ...(args.recovery ? { recovery: args.recovery } : {}),
    });
  });
}
